import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * Admin-side edit dialog for a customer profile.
 * Hits PATCH /api/admin/customers/{uid} which writes a diff to `audit_logs`.
 */
export default function EditCustomerDialog({ customer, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customer) { setForm(null); return; }
    const p = customer.profile || {};
    setForm({
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email && !customer.email.endsWith("@phone.delcochefs.local") ? customer.email : "",
      address: p.address || "",
      address_line2: p.address_line2 || "",
      city: p.city || "",
      household_size: p.household_size ?? "",
      dietary: p.dietary || "",
    });
  }, [customer]);

  if (!customer) return null;
  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required"); return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Mobile must contain at least 10 digits"); return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone,
        email: form.email || null,
        address: form.address,
        address_line2: form.address_line2,
        city: form.city,
        household_size: form.household_size === "" ? null : Number(form.household_size),
        dietary: form.dietary,
      };
      await api.patch(`/admin/customers/${customer.id}`, payload);
      toast.success("Customer updated");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-customer-dialog">
        <DialogHeader>
          <DialogTitle>Edit customer · {customer.name}</DialogTitle>
          <DialogDescription>All changes are logged in the admin audit log.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-customer-name"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Mobile</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15) })}
                onKeyDown={(e) => {
                  if (!/^[\d+]$/.test(e.key) && !["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"].includes(e.key)) e.preventDefault();
                }}
                inputMode="tel"
                data-testid="edit-customer-phone"
              />
            </div>
            <div>
              <Label>Email <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="text" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-customer-email"/>
            </div>
          </div>
          <div>
            <Label>Locality / area</Label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              onSelect={({ address }) => setForm({ ...form, address })}
              placeholder="Sector, colony, landmark"
              data-testid="edit-customer-address"
            />
          </div>
          <div>
            <Label>Flat / floor / building</Label>
            <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
              placeholder="Flat 12A, Tower B" data-testid="edit-customer-address-line2"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-customer-city"/>
            </div>
            <div>
              <Label>Household size</Label>
              <Input type="number" min={1} value={form.household_size} onChange={(e) => setForm({ ...form, household_size: e.target.value })} data-testid="edit-customer-household"/>
            </div>
          </div>
          <div>
            <Label>Dietary preferences</Label>
            <Textarea rows={2} value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} placeholder="Vegetarian, no onion, mild spice…" data-testid="edit-customer-dietary"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-primary" data-testid="edit-customer-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
